/**
 * One Euro. Pure, stateful in the small — a filter instance is a value, not a
 * side effect. Carried over from spike/index.html unchanged in behaviour.
 *
 * Do not smooth twice: a One Euro on the landmark and a setTargetAtTime on the
 * parameter are both low-passes and their group delays add. Use this only where
 * jitter would cause a MISFIRE (anything feeding a threshold).
 */

export class OneEuro {
  private x: number | null = null;
  private dx = 0;
  private t: number | null = null;
  alpha = 1;
  lagMs = 0;

  constructor(private minCutoff = 1.2, private beta = 0.05) {}

  reset(): void { this.x = null; this.dx = 0; this.t = null; this.alpha = 1; this.lagMs = 0; }

  private static a(cutoff: number, dt: number): number {
    const tau = 1 / (2 * Math.PI * cutoff);
    return 1 / (1 + tau / dt);
  }

  filter(v: number, tMs: number): number {
    if (this.t === null) { this.t = tMs; this.x = v; return v; }
    const dt = Math.max((tMs - this.t) / 1000, 1e-3);
    this.t = tMs;
    const ad = OneEuro.a(1.0, dt);
    this.dx = ad * ((v - (this.x as number)) / dt) + (1 - ad) * this.dx;
    this.alpha = OneEuro.a(this.minCutoff + this.beta * Math.abs(this.dx), dt);
    this.x = this.alpha * v + (1 - this.alpha) * (this.x as number);
    this.lagMs = ((1 - this.alpha) / this.alpha) * dt * 1000;
    return this.x;
  }
}

/**
 * Threshold with hysteresis on a CLOSING measure (engages as the value falls:
 * aperture, distance). `on` must be at least 0.10 below `off`.
 */
export function hysteresis(value: number, prev: boolean, on: number, off: number): boolean {
  if (prev) return value < off; // closing measures: aperture below `off` stays engaged
  return value < on;
}

/**
 * The rising twin: engages as the value grows (spread past a limit, height
 * past a mark). `on` must be at least 0.10 above `off`.
 */
export function rising(value: number, prev: boolean, on: number, off: number): boolean {
  if (prev) return value > off;
  return value > on;
}
